import { Bar } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto";
import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type TooltipModel,
} from "chart.js";
import { formatCurrencyShort } from "~/common/utils/currencyFormatter";
import { ChartTooltip } from "~/features/cms/homepagePersonalization/cpd/components/CPDTooltip";
import ReactDOM from "react-dom/client";
import { useEffect, useRef } from "react";
import {
  commonChartStyleProps,
  createCaret,
  createTooltipElement,
  positionTooltip,
} from "~/features/cms/homepagePersonalization/cpd/CPDUtils";
import { C_CHART_6, C_CHART_7 } from "@schaeffler-commerce/design-system";
import { styled, ThemeProvider } from "@schaeffler-commerce/design-system";
import { theme } from "~/app/theme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface LastFiveYearsPerfromanceGraphProps {
  invoice: (number | null)[];
  orderbook: (number | null)[];
  currencyCode: string;
}

export default function LastFiveYearsPerfromanceGraph({
  invoice,
  orderbook,
  currencyCode,
}: LastFiveYearsPerfromanceGraphProps) {
  const tooltipRootRef = useRef<ReactDOM.Root | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- MANAGED-DOM-REF: the refs hold a manually created tooltip element and root, and the external chart handler fills them after the first hover; the cleanup must read the values current at unmount time, so a mount-time copy stays null and removes nothing (SCHAEFF-41513)
      tooltipRootRef.current?.unmount();
      // eslint-disable-next-line react-hooks/exhaustive-deps -- MANAGED-DOM-REF: same reason as the root above
      tooltipElRef.current?.remove();
    };
  }, []);

  const data: ChartData<"bar"> = {
    labels: getYears(),
    datasets: [
      {
        label: "Invoice",
        data: invoice,
        ...commonChartStyleProps(C_CHART_6),
      },
      {
        label: "ConfirmedOrderbook",
        data: orderbook,
        ...commonChartStyleProps(C_CHART_7),
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          callback: function (value) {
            return formatCurrencyShort(value, currencyCode, "end");
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) =>
          externalTooltipHandler(
            context,
            {
              invoice,
              orderbook,
            },
            tooltipRootRef,
            tooltipElRef,
            currencyCode,
          ),
      },
    },
  };

  return (
    <ChartView>
      <Bar options={options} data={data} />
    </ChartView>
  );
}

function getYears() {
  const currentYear = new Date().getFullYear();
  const lastFiveYears = [];

  for (let i = 4; i >= 0; i--) {
    lastFiveYears.push(currentYear - i);
  }

  lastFiveYears.push(currentYear + 1);

  return lastFiveYears;
}

const externalTooltipHandler = (
  context: { chart: ChartJS; tooltip: TooltipModel<"bar"> },
  chartData: {
    invoice: (number | null)[];
    orderbook: (number | null)[];
  },
  tooltipRootRef: React.MutableRefObject<ReactDOM.Root | null>,
  tooltipElRef: React.RefObject<HTMLDivElement | null>,
  currencyCode: string,
) => {
  const { invoice = [], orderbook = [] } = chartData;
  const { chart, tooltip } = context;

  const tooltipEl = createTooltipElement(tooltipElRef);

  if (!tooltipRootRef.current) {
    tooltipRootRef.current = ReactDOM.createRoot(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  if (!dataPoint) {
    return;
  }

  const idx = dataPoint.dataIndex;

  const tooltipData = {
    invoice: invoice[idx] ?? null,
    orderbook: orderbook[idx] ?? null,
    target: null,
    gap: null,
  };

  tooltipRootRef.current.render(
    <ThemeProvider theme={theme}>
      <ChartTooltip data={tooltipData} currencyIso={currencyCode} />
    </ThemeProvider>,
  );

  requestAnimationFrame(() => {
    createCaret(tooltipEl);
    positionTooltip(tooltipEl, chart, dataPoint);
  });
};

const ChartView = styled("div")({
  height: "244px",
});
